import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, TrendingUp, AlertCircle } from 'lucide-react';
import { StockAnalysisView } from '@/components/StockAnalysisView';
import { 
  stockService, 
  FinancialHealthResponse,
  CompositeAttractivenessResponse
} from '@/services/api';
import { useEffect, useState } from 'react';
import { addRecentStock } from '@/utils/recentStocks';
import { STOCK_ATTRACTIVENESS_ENABLED } from '@/config/features';
import { createDisabledAttractivenessStub } from '@/utils/disabledAttractivenessStub';
import { HISTORICAL_PRICES_API_EXTRA_DAYS } from '@/utils/historicalPriceApiLookback';
import type { StockAnalysisLocationState } from '@/utils/betaAccess';

type AnalysisStep = 
  | 'loading'
  | 'analyzing_fundamentals' 
  | 'analyzing_sentiment'
  | 'analyzing_momentum'
  | 'complete';

const ANALYSIS_STEPS: Record<AnalysisStep, { label: string; progress: number }> = {
  loading: { label: 'Initializing analysis...', progress: 10 },
  analyzing_fundamentals: { label: 'Analyzing fundamentals...', progress: 40 },
  analyzing_sentiment: { label: 'Analyzing sentiment...', progress: 70 },
  analyzing_momentum: { label: 'Analyzing market momentum...', progress: 90 },
  complete: { label: 'Analysis complete!', progress: 100 },
};

export function StockAnalysisPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { ticker } = useParams();
  const tickerUpper = ticker?.toUpperCase() || '';
  const showBetaRestrictionNotice =
    (location.state as StockAnalysisLocationState | null)?.showBetaRestrictionNotice ?? false;
  
  const [financialHealth, setFinancialHealth] = useState<FinancialHealthResponse | null>(null);
  const [attractivenessScore, setAttractivenessScore] = useState<CompositeAttractivenessResponse | null>(null);
  const [stockPrice, setStockPrice] = useState<{
    price: number;
    change: number;
    changePercent: number;
    companyName: string;
    sector?: string;
    industry?: string;
  } | null>(null);
  const [historicalPrices, setHistoricalPrices] = useState<Array<{
    date: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
    percent_change: number | null;
  }> | null>(null);
  const [significantMoves, setSignificantMoves] = useState<Array<{
    date: string;
    percent_change: number;
  }> | null>(null);
  const [originEventDates, setOriginEventDates] = useState<string[]>([]);
  const blockTradeDates: string[] | null = null;
  const blockTradeNotionalByDate: Record<string, number> | null = null;
  const [largestBlockTrades, setLargestBlockTrades] = useState<Array<{
    date: string;
    time: string;
    price: number;
    size: number;
    dollar_value: number;
    side: string;
  }> | null>(null);
  const [largestBlockTradePerMonth, setLargestBlockTradePerMonth] = useState<Array<{
    month: string;
    trade: { date: string; time: string; price: number; size: number; dollar_value: number; side: string };
  }> | null>(null);
  /** One largest qualifying block trade per session date (from largest_block_trades.json when regenerated). */
  const [largestBlockTradePerDay, setLargestBlockTradePerDay] = useState<Array<{
    date: string;
    time: string;
    price: number;
    size: number;
    dollar_value: number;
    side: string;
  }> | null>(null);
  /** IQR upper-fence outlier subset of largestBlockTradePerDay — only genuinely notable prints. */
  const [noteworthyBlockTradesPerDay, setNoteworthyBlockTradesPerDay] = useState<Array<{
    date: string;
    time: string;
    price: number;
    size: number;
    dollar_value: number;
    side: string;
  }> | null>(null);
  /** noteworthy ∪ significant-price/volume days — primary source for chart diamonds. */
  const [diamondBlockTradesPerDay, setDiamondBlockTradesPerDay] = useState<Array<{
    date: string;
    time: string;
    price: number;
    size: number;
    dollar_value: number;
    side: string;
  }> | null>(null);
  const [showFullLoadingScreen, setShowFullLoadingScreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [analysisStep, setAnalysisStep] = useState<AnalysisStep>('loading');
  const [error, setError] = useState<string | null>(null);

  // Helper function to process API results (extracted for reuse with cache)
  const processResults = (scoreResult: any, priceResult: any) => {
    // Set attractiveness data (required for main view)
    if (scoreResult) {
      // Require layer_breakdown + financial_health object; empty components means
      // backend fundamentals failed (e.g. missing filings) — still show chart + other layers.
      const fhLayer = scoreResult.layer_breakdown?.financial_health;
      if (!scoreResult.layer_breakdown || !fhLayer) {
        console.warn(`Incomplete attractiveness data for ${tickerUpper}, layer_breakdown missing`);
        setError(`Attractiveness data not complete for ${tickerUpper}`);
      } else {
        if (!fhLayer.components || fhLayer.components.length === 0) {
          console.warn(
            `Fundamentals metrics missing for ${tickerUpper} (financial_health.components empty) — showing partial analysis`
          );
        }
        setAttractivenessScore(scoreResult);
        const fhMetadata = fhLayer.metadata;
        const hasFhSectorInfo =
          Boolean(fhMetadata?.sector_name?.trim()) || Boolean(fhMetadata?.industry_name?.trim());
        setFinancialHealth({
          symbol: tickerUpper,
          layer_name: 'financial_health',
          composite_score: fhLayer.score,
          academic_score: fhLayer.academic_score,
          academic_grade: fhLayer.academic_grade,
          interpretation: fhLayer.interpretation as any,
          components: fhLayer.components || [],
          trend: fhLayer.trend,
          trend_data: null,
          timestamp: scoreResult.timestamp,
          metadata: (hasFhSectorInfo
            ? fhMetadata
            : {
                sector_type: 'unknown',
                sector_name: priceResult?.sector?.trim() || 'Unknown',
                industry_name: priceResult?.industry?.trim() || 'Unknown',
                model_name: fhMetadata?.model_name || 'Unknown',
                model_description: fhMetadata?.model_description || '',
                limitations: fhMetadata?.limitations || [],
                use_sector_models: fhMetadata?.use_sector_models ?? true,
                academic_score: fhLayer.academic_score,
                academic_grade: fhLayer.academic_grade,
              }) as any,
          error: null
        });

        // Profile endpoint can fail or omit `name` while attractiveness exists — without a quote the page
        // would finish loading but stay on the skeleton forever (main view gates on stockPrice).
        if (!priceResult) {
          console.warn(
            `Stock profile/quote unavailable for ${tickerUpper}; showing analysis with placeholder quote`
          );
          setStockPrice({
            price: 0,
            change: 0,
            changePercent: 0,
            companyName: tickerUpper,
          });
        }
      }
    } else {
      setError(`Attractiveness data not available for ${tickerUpper}`);
    }

    // Set stock price from profile when available (overrides placeholder above)
    if (priceResult) {
      setStockPrice(priceResult);
    }
  };

  useEffect(() => {
    let loadingTimeout: NodeJS.Timeout;
    let isMounted = true;
    
    async function fetchData() {
      if (!tickerUpper) {
        setError('No ticker provided');
        setIsLoading(false);
        return;
      }
      
      // Track this stock visit
      addRecentStock(tickerUpper);
      
      setError(null);
      setIsLoading(true);
      setAnalysisStep('loading');
      
      // Always fetch fresh data from backend to immediately reflect orchestrator.py runs
      // Backend has its own disk cache so this is still fast (<100ms)
      
      // Only show full loading screen if fetch takes > 5 seconds (actually generating)
      loadingTimeout = setTimeout(() => {
        if (isMounted) {
          setShowFullLoadingScreen(true);
          setAnalysisStep('analyzing_fundamentals');
        }
      }, 5000);
      
      try {
        const attractivenessPromise = STOCK_ATTRACTIVENESS_ENABLED
          ? stockService.getAttractivenessScore(tickerUpper)
          : Promise.resolve(createDisabledAttractivenessStub(tickerUpper).scoreResult);

        // Fetch all data in parallel for speed
        const [scoreResult, priceResult, historicalResult, significantMovesResult, largestBlockTradesResult, originEventDatesResult] = await Promise.all([
          attractivenessPromise,
          stockService.getStockPrice(tickerUpper),
          stockService.getHistoricalPrices(tickerUpper, 365 + HISTORICAL_PRICES_API_EXTRA_DAYS),
          stockService.getSignificantMoves(tickerUpper, 365, 10),
          stockService.getLargestBlockTrades(tickerUpper),    // preferred for block trade overlay
          stockService.getOriginEventDates(tickerUpper),
        ]);
        
        if (!isMounted) return;
        
        // If we're showing the full loading screen, continue the step progression
        if (showFullLoadingScreen) {
          setAnalysisStep('analyzing_sentiment');
          await new Promise(resolve => setTimeout(resolve, 1500));
          if (!isMounted) return;
          setAnalysisStep('analyzing_momentum');
          await new Promise(resolve => setTimeout(resolve, 1200));
          if (!isMounted) return;
          setAnalysisStep('complete');
          await new Promise(resolve => setTimeout(resolve, 300));
        }

        // Process the results using shared helper
        processResults(scoreResult, priceResult);
        
        // Set historical prices if available
        if (historicalResult) {
          setHistoricalPrices(historicalResult);
        }
        
        // Set significant moves if available
        if (significantMovesResult) {
          setSignificantMoves(significantMovesResult);
        }

        if (largestBlockTradesResult?.top_block_trades) {
          setLargestBlockTrades(largestBlockTradesResult.top_block_trades);
        }
        if (largestBlockTradesResult?.largest_block_trade_per_month) {
          setLargestBlockTradePerMonth(largestBlockTradesResult.largest_block_trade_per_month);
        }
        if (largestBlockTradesResult?.largest_block_trade_per_day?.length) {
          setLargestBlockTradePerDay(largestBlockTradesResult.largest_block_trade_per_day);
        } else {
          setLargestBlockTradePerDay(null);
        }
        if (largestBlockTradesResult?.noteworthy_block_trades_per_day?.length) {
          setNoteworthyBlockTradesPerDay(largestBlockTradesResult.noteworthy_block_trades_per_day);
        } else {
          setNoteworthyBlockTradesPerDay(null);
        }
        if (largestBlockTradesResult?.diamond_block_trades_per_day?.length) {
          setDiamondBlockTradesPerDay(largestBlockTradesResult.diamond_block_trades_per_day);
        } else {
          setDiamondBlockTradesPerDay(null);
        }
        setOriginEventDates(originEventDatesResult ?? []);
        
      } catch (err) {
        if (isMounted) {
          setError(`Failed to load data for ${tickerUpper}`);
          console.error(err);
        }
      } finally {
        // Clear the loading timeout
        clearTimeout(loadingTimeout);
        if (isMounted) {
          setShowFullLoadingScreen(false);
          setIsLoading(false);
        }
      }
    }
    
    fetchData();
    
    return () => {
      isMounted = false;
      clearTimeout(loadingTimeout);
    };
  }, [tickerUpper]);

  // If we have attractiveness data, show the view with smooth fade-in
  if (financialHealth && attractivenessScore && stockPrice && !isLoading) {
    const stockData = {
      company: stockPrice.companyName,
      ticker: tickerUpper,
      currentPrice: `$${stockPrice.price.toFixed(2)}`,
      change: `${stockPrice.change >= 0 ? '+' : ''}${stockPrice.change.toFixed(2)}`,
      changePercent: `${stockPrice.changePercent >= 0 ? '+' : ''}${stockPrice.changePercent.toFixed(2)}%`,
      sector: stockPrice.sector,
      industry: stockPrice.industry,
    };

    return (
      <StockAnalysisView
        onBack={() => navigate('/')}
        showBetaRestrictionNotice={showBetaRestrictionNotice}
        stockData={stockData}
        financialHealth={financialHealth}
        compositeScore={attractivenessScore}
        historicalPrices={historicalPrices}
        significantMoves={significantMoves}
        blockTradeDates={blockTradeDates}
        blockTradeNotionalByDate={blockTradeNotionalByDate}
        largestBlockTrades={largestBlockTrades}
        largestBlockTradePerDay={largestBlockTradePerDay}
        noteworthyBlockTradesPerDay={noteworthyBlockTradesPerDay}
        diamondBlockTradesPerDay={diamondBlockTradesPerDay}
        largestBlockTradePerMonth={largestBlockTradePerMonth}
        originEventDates={originEventDates}
      />
    );
  }

  // Full loading screen - only shown if fetch takes > 5 seconds (generating new analysis)
  if (showFullLoadingScreen) {
    const currentStep = ANALYSIS_STEPS[analysisStep];
    
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-lg px-6"
        >
          {/* Animated Icon */}
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{
              repeat: Infinity,
              duration: 2,
              ease: "easeInOut",
            }}
            className="mb-8 mx-auto w-20 h-20 bg-gray-900 rounded-2xl flex items-center justify-center"
          >
            <TrendingUp className="w-10 h-10 text-white" />
          </motion.div>

          {/* Ticker */}
          <h2 className="text-3xl font-semibold mb-3 tracking-tight">
            Analyzing {tickerUpper}
          </h2>

          {/* Current Step */}
          <p className="text-gray-600 mb-8">
            {error ? (
              <span className="text-red-500">{error}</span>
            ) : (
              currentStep.label
            )}
          </p>

          {/* Progress Bar */}
          {!error && (
            <div className="w-full max-w-sm mx-auto mb-8">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gray-900 rounded-full"
                  initial={{ width: '0%' }}
                  animate={{ width: `${currentStep.progress}%` }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                />
              </div>
              <p className="text-gray-400 text-sm mt-2">{currentStep.progress}%</p>
            </div>
          )}

          {/* Loading Dots */}
          {!error && (
            <div className="flex gap-2 justify-center mb-8">
              <motion.div
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ repeat: Infinity, duration: 1.5, delay: 0 }}
                className="w-2 h-2 bg-gray-900 rounded-full"
              />
              <motion.div
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ repeat: Infinity, duration: 1.5, delay: 0.2 }}
                className="w-2 h-2 bg-gray-900 rounded-full"
              />
              <motion.div
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ repeat: Infinity, duration: 1.5, delay: 0.4 }}
                className="w-2 h-2 bg-gray-900 rounded-full"
              />
            </div>
          )}

          {/* Cancel Button */}
          <button
            onClick={() => navigate('/')}
            className="text-gray-500 hover:text-gray-900 transition-colors text-sm"
          >
            Back
          </button>
          
          {/* Helpful Note */}
          {!error && analysisStep !== 'loading' && (
            <p className="text-gray-400 text-xs mt-6">
              First-time analysis may take 30-60 seconds as we compute comprehensive metrics
            </p>
          )}
        </motion.div>
      </div>
    );
  }

  // Error state
  if (error && !isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <motion.header
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="bg-white border-b border-gray-200"
        >
          <div className="max-w-7xl mx-auto px-6 py-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            
            <div className="flex items-center gap-3">
              <TrendingUp className="w-10 h-10 text-blue-500" strokeWidth={1.5} />
              <div>
                <h1 className="tracking-tight">Stock Analysis - {tickerUpper}</h1>
                <p className="text-gray-600">Comprehensive Attractiveness Analysis</p>
              </div>
            </div>
          </div>
        </motion.header>

        {/* Main Content - Error State */}
        <main className="max-w-7xl mx-auto px-6 py-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-4 text-center">
                <AlertCircle className="w-12 h-12 text-amber-500" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">Data Not Available</h2>
                  <p className="text-gray-600">{error}</p>
                  <p className="text-gray-500 text-sm mt-2">
                    Please try again or contact support if the issue persists.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </main>
      </div>
    );
  }

  // Default: minimal loading state while waiting for cached data
  // This shows the page structure immediately so it feels responsive
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header skeleton */}
      <motion.header
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="bg-white"
      >
        <div className="max-w-6xl mx-auto px-8 py-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-400 hover:text-gray-700 transition-colors mb-12"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          {/* Stock Info skeleton */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="px-4 py-1.5 bg-gray-100 rounded-xl text-gray-400">
                {tickerUpper}
              </span>
            </div>
            <div className="h-12 w-64 bg-gray-100 rounded-lg animate-pulse mb-2" />
            <div className="h-6 w-24 bg-gray-100 rounded animate-pulse" />
          </div>
        </div>
      </motion.header>

      {/* Content skeleton */}
      <main className="max-w-6xl mx-auto px-8 py-16">
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2, delay: i * 0.05 }}
              className="bg-white rounded-3xl p-10"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="h-6 w-32 bg-gray-100 rounded animate-pulse" />
                <div className="h-10 w-16 bg-gray-100 rounded animate-pulse" />
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full" />
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );
}

